import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { errorMessage, fetchHandovers, FilterOptions, formatDateTime, HandoverPage, PackingFilters } from './packingApi';
import { btn, input, num, td, th } from './packingStyles';

interface Props {
  refreshKey: number;
  filters: PackingFilters;
  onOptions: (options: FilterOptions) => void;
}

/** Green: everything produced so far is packed; orange: part of it still waits at the station. */
const STATUS_COLORS = {
  complete: { bg: '#dcfce7', edge: '#16a34a', text: '#166534' },
  partial: { bg: '#ffedd5', edge: '#ea580c', text: '#9a3412' },
} as const;

/** "Przekazane do magazynu": one row per position with what was handed over, who and when. */
const PackingHistoryView: React.FC<Props> = ({ refreshKey, filters, onOptions }) => {
  const { t } = useLanguage();
  const [date, setDate] = useState('');
  const [userId, setUserId] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<HandoverPage | null>(null);
  const [error, setError] = useState<string | null>(null);

  // A filter change from the bar above starts again from the first page (adjusted during render,
  // so no request goes out for the old page).
  const [shownFilters, setShownFilters] = useState(filters);
  if (shownFilters !== filters) {
    setShownFilters(filters);
    setPage(1);
  }

  useEffect(() => {
    // Only the newest request may fill the table (filters, paging and live refreshes overlap).
    let current = true;
    fetchHandovers(date, userId, status, filters, page)
      .then((result) => {
        if (!current) return;
        setData(result);
        onOptions(result.meta.filters);
        setError(null);
      })
      .catch((e: any) => {
        if (current) setError(errorMessage(e, t('packing.errorLoad')));
      });
    return () => {
      current = false;
    };
  }, [date, userId, status, filters, page, refreshKey, onOptions, t]);

  const filterLabel: React.CSSProperties = { display: 'flex', flexDirection: 'column', fontSize: 13, color: '#475569', fontWeight: 600 };

  return (
    <>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <label style={filterLabel}>
          {t('packing.history.date')}
          <input type="date" value={date} onChange={(e) => { setDate(e.target.value); setPage(1); }} style={{ ...input, marginTop: 4, width: 180 }} />
        </label>
        <label style={filterLabel}>
          {t('packing.history.user')}
          <select value={userId} onChange={(e) => { setUserId(e.target.value); setPage(1); }} style={{ ...input, marginTop: 4, width: 220 }}>
            <option value="">{t('packing.history.allUsers')}</option>
            {data?.meta.users.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </label>
        <label style={filterLabel}>
          {t('packing.history.status')}
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} style={{ ...input, marginTop: 4, width: 220 }}>
            <option value="">{t('packing.history.allStatuses')}</option>
            <option value="complete">{t('packing.history.complete')}</option>
            <option value="partial">{t('packing.history.partial')}</option>
          </select>
        </label>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: '#475569', marginBottom: 12 }}>
        {(['complete', 'partial'] as const).map((s) => (
          <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 14, height: 14, borderRadius: 4, background: STATUS_COLORS[s].bg, border: `2px solid ${STATUS_COLORS[s].edge}` }} />
            {t(`packing.history.${s}Hint`)}
          </span>
        ))}
      </div>
      {error && <div style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</div>}
      {data && data.data.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>{t('packing.history.empty')}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr>
                <th style={{ ...th, paddingLeft: 14 }}>{t('packing.history.status')}</th>
                <th style={th}>{t('packing.history.when')}</th>
                <th style={th}>{t('packing.history.user')}</th>
                <th style={th}>{t('packing.col.weekLine')}</th>
                <th style={th}>{t('packing.col.index')}</th>
                <th style={th}>{t('packing.col.name')}</th>
                <th style={th}>{t('packing.col.brand')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('packing.history.handedOver')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('packing.history.packedOfPlan')}</th>
              </tr>
            </thead>
            <tbody>
              {data?.data.map((h) => {
                const colors = STATUS_COLORS[h.status];
                return (
                  <tr key={h.pivot_id} style={{ background: colors.bg, boxShadow: `inset 5px 0 0 ${colors.edge}` }}>
                    <td style={{ ...td, paddingLeft: 14, fontWeight: 800, color: colors.text, whiteSpace: 'nowrap' }}>
                      {t(`packing.history.${h.status}`)}
                      {h.status === 'partial' && h.to_pack > 0 && (
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{t('packing.history.waiting', { n: h.to_pack })}</div>
                      )}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatDateTime(h.last_at)}</td>
                    <td style={td}>{h.users.join(', ') || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 700 }}>{t('packing.col.week')} {h.tydzien}</div>
                      <div style={{ fontSize: 12, color: '#475569' }}>{[h.linia, h.location].filter(Boolean).join(' · ')}</div>
                    </td>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{h.indeks}</td>
                    <td style={td}>{h.nazwa}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{h.marka || '—'}</td>
                    <td style={{ ...num, fontSize: 16, fontWeight: 800 }}>{h.handed_over}</td>
                    <td style={num}>{h.packed} / {h.planned}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {data && data.meta.last_page > 1 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginTop: 12 }}>
          <button type="button" style={btn('ghost', true)} disabled={page <= 1} onClick={() => setPage(page - 1)}>{t('packing.prev')}</button>
          <span style={{ fontSize: 14 }}>{t('packing.page', { page, last: data.meta.last_page })}</span>
          <button type="button" style={btn('ghost', true)} disabled={page >= data.meta.last_page} onClick={() => setPage(page + 1)}>{t('packing.next')}</button>
        </div>
      )}
    </>
  );
};

export default PackingHistoryView;
